import type { CleaningTaskQueueResponse } from '@/shared/api';

export type CleaningDayFilter = 'all' | 'free' | 'active' | 'overdue' | 'completed';

export function cleaningDayCounts(tasks: CleaningTaskQueueResponse[], date: string) {
  return {
    free: tasks.filter((task) => task.status === 'Pending' && !task.assignedToUserId).length,
    arrivals: tasks.filter((task) => task.nextArrival?.checkInDate === date).length,
    overdue: tasks.filter((task) => task.isOverdue).length,
    completed: tasks.filter((task) => task.status === 'Completed').length,
  };
}

export function filterCleaningDay(tasks: CleaningTaskQueueResponse[], filter: CleaningDayFilter) {
  const filtered = tasks.filter((task) => {
    if (filter === 'free') return task.status === 'Pending' && !task.assignedToUserId;
    if (filter === 'active') return task.status === 'InProgress' || task.status === 'Issue';
    if (filter === 'overdue') return task.isOverdue;
    if (filter === 'completed') return task.status === 'Completed';
    return task.status !== 'Cancelled';
  });
  return [...filtered].sort(
    (left, right) =>
      operationalPriority(left) - operationalPriority(right) ||
      left.scheduledFromLocalTime.localeCompare(right.scheduledFromLocalTime) ||
      left.propertyName.localeCompare(right.propertyName, 'de'),
  );
}

export function cleaningDayStatus(task: CleaningTaskQueueResponse, currentUserId?: string) {
  if (task.isOverdue) return '\u00dcberf\u00e4llig';
  if (task.status === 'Issue') return 'Problem gemeldet';
  if (task.status === 'Completed') return 'Erledigt';
  if (task.status === 'Pending' && !task.assignedToUserId) return 'Frei';
  if (task.status === 'InProgress') {
    if (task.assignedToUserId === currentUserId) return 'Von mir \u00fcbernommen';
    return task.assignedToUserName ? `${task.assignedToUserName} reinigt` : 'In Reinigung';
  }
  const labels = {
    Pending: 'Zugewiesen',
    InProgress: 'In Reinigung',
    Completed: 'Erledigt',
    Issue: 'Problem gemeldet',
    Cancelled: 'Entf\u00e4llt',
  } as const;
  return labels[task.status];
}

function operationalPriority(task: CleaningTaskQueueResponse) {
  if (task.isOverdue) return 0;
  if (task.nextArrival?.checkInDate === task.scheduledFor) return 1;
  if (task.status === 'InProgress' || task.status === 'Issue') return 2;
  if (task.status === 'Pending') return 3;
  if (task.status === 'Completed') return 4;
  return 5;
}

export function arrivalLabel(task: CleaningTaskQueueResponse, selectedDate: string) {
  const arrival = task.nextArrival;
  if (!arrival) return 'Keine Anreise bekannt';
  const when =
    arrival.checkInDate === selectedDate ? 'heute' : formatShortDate(arrival.checkInDate);
  return arrival.status === 'Pending' ? `Vorgemerkte Anreise ${when}` : `Neue Anreise ${when}`;
}

export function assigneeOptions(
  tasks: Array<Pick<CleaningTaskQueueResponse, 'assignedToUserId' | 'assignedToUserName'>>,
) {
  const names = new Map<string, string>();
  for (const task of tasks) {
    if (task.assignedToUserId && task.assignedToUserName)
      names.set(task.assignedToUserId, task.assignedToUserName);
  }
  return [...names]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

function formatShortDate(iso: string) {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}
